import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Users,
  Crown,
  DollarSign,
  TrendingUp,
  Filter,
  Search,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  BarChart3,
  Calendar,
  MessageSquare,
  Phone,
  Mail,
  Settings,
  Download,
  RefreshCw,
  Edit,
  Plus,
  Trash2,
  Save,
  Shield,
  Package,
  X,
  Lock,
  Image as ImageIcon,
} from 'lucide-react';

const AdminPanel = () => {
  const { isDarkMode } = useDarkMode();
  const { user } = useAuth();

  // بررسی سطح دسترسی کاربر جاری
  const userPermissions = user?.permissions;
  const canAccess = (perm) => {
    if (!userPermissions) return true; // دسترسی کامل برای ادمین‌های اصلی (parham, parsa)
    if (Array.isArray(userPermissions)) return userPermissions.includes(perm);
    return true;
  };

  const canAccessDashboard = canAccess('dashboard');
  const canAccessPurchases = canAccess('purchases');
  const canAccessDiscountCodes = canAccess('discountCodes');
  const canAccessUsers = canAccess('users');
  const canAccessTickets = canAccess('tickets');

  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const decoded = JSON.parse(atob(token.split('.')[1]));
        if (decoded?.permissions && Array.isArray(decoded.permissions)) {
          if (!decoded.permissions.includes('dashboard')) {
            if (decoded.permissions.includes('tickets')) return 'tickets';
            if (decoded.permissions.includes('users')) return 'users';
          }
        }
      }
    } catch {}
    return 'dashboard';
  });

  useEffect(() => {
    if (user?.permissions && Array.isArray(user.permissions)) {
      if (!canAccessDashboard && activeTab === 'dashboard') {
        if (canAccessTickets) setActiveTab('tickets');
        else if (canAccessUsers) setActiveTab('users');
      }
    }
  }, [user, canAccessDashboard]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingPurchases, setIsLoadingPurchases] = useState(false);
  const [stats, setStats] = useState(null);
  const [recentPurchases, setRecentPurchases] = useState([]);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [isLoadingDiscountCodes, setIsLoadingDiscountCodes] = useState(false);
  const [showDiscountCodeModal, setShowDiscountCodeModal] = useState(false);
  const [selectedDiscountCode, setSelectedDiscountCode] = useState(null);
  const [discountCodeStats, setDiscountCodeStats] = useState(null);
  const [notesModal, setNotesModal] = useState({
    open: false,
    status: '',
    title: '',
    color: '',
    required: false
  });
  const [adminNote, setAdminNote] = useState('');
  const [dailyRevenue, setDailyRevenue] = useState([]);
  const [isLoadingChart, setIsLoadingChart] = useState(false);
  const [hoveredDay, setHoveredDay] = useState(null);

  // تیکت‌ها
  const [adminTickets, setAdminTickets] = useState([]);
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);
  const [ticketFilter, setTicketFilter] = useState({ status: 'all', department: 'all', priority: 'all' });
  const [adminTicketsPage, setAdminTicketsPage] = useState(1);
  const [adminTicketsTotalPages, setAdminTicketsTotalPages] = useState(1);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [adminReplyText, setAdminReplyText] = useState('');
  const [adminSelectedImage, setAdminSelectedImage] = useState(null);
  const [adminImagePreview, setAdminImagePreview] = useState(null);
  const [adminActiveZoomImage, setAdminActiveZoomImage] = useState(null);
  const adminFileRef = useRef(null);

  useEffect(() => {
    if (selectedTicket || adminActiveZoomImage) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedTicket, adminActiveZoomImage]);

  const formatMessageTime = (dateStr) => {
    try {
      if (!dateStr) return '';
      const normalized = dateStr.includes('T') || dateStr.includes('Z') ? dateStr : dateStr.replace(' ', 'T') + 'Z';
      const d = new Date(normalized);
      return new Intl.DateTimeFormat('fa-IR', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Asia/Tehran'
      }).format(d);
    } catch {
      return '';
    }
  };

  const getMessageDateDay = (dateStr) => {
    try {
      if (!dateStr) return '';
      const normalized = dateStr.includes('T') || dateStr.includes('Z') ? dateStr : dateStr.replace(' ', 'T') + 'Z';
      const d = new Date(normalized);
      return new Intl.DateTimeFormat('fa-IR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'Asia/Tehran'
      }).format(d);
    } catch {
      return '';
    }
  };

  const formatChartDate = (dateStr) => {
    try {
      if (!dateStr) return '';
      const dateObj = new Date(dateStr + 'T00:00:00Z');
      return new Intl.DateTimeFormat('fa-IR', {
        month: 'long',
        day: 'numeric'
      }).format(dateObj);
    } catch (e) {
      return dateStr;
    }
  };

  const getPaginationPages = (current, total) => {
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    if (current <= 4) {
      return [1, 2, 3, 4, 5, '...', total];
    }
    if (current >= total - 3) {
      return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
    }
    return [1, '...', current - 1, current, current + 1, '...', total];
  };

  useEffect(() => {
    if (activeTab === 'dashboard' && canAccessDashboard) {
      fetchStats();
      fetchDailyRevenue();
    }
    if (activeTab === 'users' && canAccessUsers) {
      fetchUsers();
    }
    if (activeTab === 'purchases' && canAccessPurchases) {
      fetchPurchases();
    }
    if (activeTab === 'discountCodes' && canAccessDiscountCodes) {
      fetchDiscountCodes();
    }
    if (activeTab === 'tickets' && canAccessTickets) {
      fetchAdminTickets();
    }
  }, [activeTab, canAccessDashboard, canAccessUsers, canAccessPurchases, canAccessDiscountCodes, canAccessTickets]);

  const adminTicketsPageRef = useRef(adminTicketsPage);
  useEffect(() => {
    adminTicketsPageRef.current = adminTicketsPage;
  }, [adminTicketsPage]);

  // پاولینگ زنده ۳ ثانیه‌ای برای آپدیت اتوماتیک پیام‌های تیکت در پنل ادمین
  useEffect(() => {
    if (activeTab !== 'tickets') return;
    const interval = setInterval(() => {
      fetchAdminTickets(ticketFilter, adminTicketsPageRef.current, true);
      if (selectedTicket?.id) {
        fetchTicketDetail(selectedTicket.id, true);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [activeTab, selectedTicket, ticketFilter]);

  const fetchStats = async () => {
    setIsLoadingStats(true);
    try {
      const response = await axios.get('/api/admin/stats');
      setStats(response.data.stats);
      setRecentPurchases(response.data.recentPurchases);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('خطا در دریافت آمار');
    } finally {
      setIsLoadingStats(false);
    }
  };

  const fetchDailyRevenue = async () => {
    setIsLoadingChart(true);
    try {
      const response = await axios.get('/api/admin/daily-revenue?days=30');
      setDailyRevenue(response.data.data || []);
    } catch (error) {
      console.error('Error fetching daily revenue:', error);
    } finally {
      setIsLoadingChart(false);
    }
  };

  const fetchUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const response = await axios.get('/api/admin/users');
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('خطا در دریافت کاربران');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const fetchPurchases = async () => {
    setIsLoadingPurchases(true);
    try {
      const response = await axios.get('/api/admin/purchases?limit=5000');
      setPurchases(response.data.purchases || []);
    } catch (error) {
      console.error('Error fetching purchases:', error);
      toast.error('خطا در دریافت خریدها');
    } finally {
      setIsLoadingPurchases(false);
    }
  };

  const fetchDiscountCodes = async () => {
    setIsLoadingDiscountCodes(true);
    try {
      const [codesResponse, statsResponse] = await Promise.all([
        axios.get('/api/discount-codes/admin'),
        axios.get('/api/discount-codes/admin/stats')
      ]);
      setDiscountCodes(codesResponse.data.discountCodes);
      setDiscountCodeStats(statsResponse.data.stats);
    } catch (error) {
      console.error('Error fetching discount codes:', error);
      toast.error('خطا در دریافت کدهای تخفیف');
    } finally {
      setIsLoadingDiscountCodes(false);
    }
  };

  const fetchAdminTickets = async (filters = ticketFilter, pageNum = adminTicketsPage, isSilent = false) => {
    if (!isSilent) setIsLoadingTickets(true);
    try {
      const params = new URLSearchParams({ page: pageNum, limit: 12 });
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.department !== 'all') params.append('department', filters.department);
      if (filters.priority !== 'all') params.append('priority', filters.priority);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/tickets/admin/all?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminTickets(response.data.tickets || []);
      setAdminTicketsTotalPages(response.data.totalPages || 1);
    } catch (error) {
      if (!isSilent) {
        console.error('Error fetching tickets:', error);
        toast.error('خطا در دریافت تیکت‌ها');
      }
    } finally {
      if (!isSilent) setIsLoadingTickets(false);
    }
  };

  const fetchTicketDetail = async (ticketId, isSilent = false) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTicket(res.data.ticket);
      setTicketMessages(res.data.messages || []);
    } catch (error) {
      if (!isSilent) toast.error('خطا در بارگذاری تیکت');
    }
  };

  const handleAdminTicketReply = async () => {
    if ((!adminReplyText.trim() && !adminSelectedImage) || !selectedTicket) return;
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      if (adminReplyText.trim()) formData.append('message', adminReplyText.trim());
      if (adminSelectedImage) formData.append('image', adminSelectedImage);

      await axios.post(`/api/tickets/${selectedTicket.id}/messages`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminReplyText('');
      setAdminSelectedImage(null);
      setAdminImagePreview(null);
      if (adminFileRef.current) adminFileRef.current.value = '';
      await fetchTicketDetail(selectedTicket.id);
      fetchAdminTickets(ticketFilter, true);
      toast.success('پاسخ ارسال شد');
    } catch (error) {
      toast.error('خطا در ارسال پاسخ');
    }
  };

  const handleAdminTicketStatus = async (ticketId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`/api/tickets/${ticketId}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('وضعیت تیکت بروزرسانی شد');
      if (selectedTicket?.id === ticketId) {
        await fetchTicketDetail(ticketId);
      }
      fetchAdminTickets();
    } catch (error) {
      toast.error('خطا در بروزرسانی وضعیت');
    }
  };

  const adminChatContainerRef = useRef(null);

  useEffect(() => {
    if (selectedTicket && ticketMessages.length > 0 && adminChatContainerRef.current) {
      adminChatContainerRef.current.scrollTop = adminChatContainerRef.current.scrollHeight;
    }
  }, [selectedTicket?.id, ticketMessages.length]);

  const handleDeleteTicket = async (ticketId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('آیا از حذف این تیکت و تمامی پیام‌های آن اطمینان دارید؟')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/tickets/admin/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('تیکت با موفقیت حذف شد');
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(null);
        setTicketMessages([]);
      }
      fetchAdminTickets(ticketFilter);
    } catch (error) {
      toast.error('خطا در حذف تیکت');
    }
  };

  const handleCreateDiscountCode = async (discountData) => {
    try {
      await axios.post('/api/discount-codes/admin', discountData);
      toast.success('کد تخفیف با موفقیت ایجاد شد');
      fetchDiscountCodes();
      setShowDiscountCodeModal(false);
      setSelectedDiscountCode(null);
    } catch (error) {
      console.error('Error creating discount code:', error);
      toast.error(error.response?.data?.message || 'خطا در ایجاد کد تخفیف');
    }
  };

  const handleUpdateDiscountCode = async (id, updateData) => {
    try {
      await axios.put(`/api/discount-codes/admin/${id}`, updateData);
      toast.success('کد تخفیف با موفقیت به‌روزرسانی شد');
      fetchDiscountCodes();
      setShowDiscountCodeModal(false);
      setSelectedDiscountCode(null);
    } catch (error) {
      console.error('Error updating discount code:', error);
      toast.error(error.response?.data?.message || 'خطا در به‌روزرسانی کد تخفیف');
    }
  };

  const openNotesModal = (status) => {
    const notes = window.prompt(
        status === 'rejected'
            ? 'دلیل رد خرید را بنویسید (الزامی):'
            : 'یادداشت ادمین (اختیاری):'
    );
    if (status === 'rejected' && !notes?.trim()) {
      toast.error('برای رد خرید باید دلیل بنویسید!');
      return;
    }
    handleStatusChange(selectedPurchase.id, status, notes || '');
  };

  const handleDeleteDiscountCode = async (id) => {
    if (window.confirm('آیا مطمئن هستید که می‌خواهید این کد تخفیف را حذف کنید؟')) {
      try {
        await axios.delete(`/api/discount-codes/admin/${id}`);
        toast.success('کد تخفیف با موفقیت حذف شد');
        fetchDiscountCodes();
      } catch (error) {
        console.error('Error deleting discount code:', error);
        toast.error('خطا در حذف کد تخفیف');
      }
    }
  };

  const handleToggleDiscountCode = async (id) => {
    try {
      await axios.patch(`/api/discount-codes/admin/${id}/toggle`);
      toast.success('وضعیت کد تخفیف تغییر کرد');
      fetchDiscountCodes();
    } catch (error) {
      console.error('Error toggling discount code:', error);
      toast.error('خطا در تغییر وضعیت کد تخفیف');
    }
  };



  const handleDeleteUser = async (userId) => {
    if (window.confirm('آیا مطمئن هستید که می‌خواهید این کاربر را حذف کنید؟ این عمل غیرقابل بازگشت است.')) {
      try {
        await axios.delete(`/api/admin/users/${userId}`);
        toast.success('کاربر با موفقیت حذف شد');
        fetchUsers(); // Refresh users list
        setShowUserModal(false); // Close modal if open
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error('خطا در حذف کاربر');
      }
    }
  };

  const handleStatusChange = async (purchaseId, newStatus, notes) => {
    setIsLoading(true);
    try {
      await axios.patch(`/api/admin/purchases/${purchaseId}/status`, {
        status: newStatus,
        adminNotes: notes
      });
      toast.success('وضعیت با موفقیت به‌روزرسانی شد');
      fetchPurchases(); // Refresh purchases list
      setShowModal(false);
      setSelectedPurchase(null);
    } catch (error) {
      console.error('Error updating purchase status:', error);
      toast.error('خطا در به‌روزرسانی وضعیت');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (date) => {
    try {
      if (!date) return 'نامشخص';
      let dateObj;
      if (typeof date === 'string') {
        // SQLite datetime is stored as UTC — add Z if no timezone info
        const normalized = date.includes('T') || date.includes('Z') ? date : date.replace(' ', 'T') + 'Z';
        dateObj = new Date(normalized);
      } else {
        dateObj = new Date(date);
      }
      if (isNaN(dateObj.getTime())) return 'نامشخص';
      return new Intl.DateTimeFormat('fa-IR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Asia/Tehran'
      }).format(dateObj);
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'نامشخص';
    }
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('fa-IR').format(amount) + ' تومان';
  };

  const formatFileSize = (size) => {
    return size;
  };

  const getStatusColor = (status) => {
    if (isDarkMode) {
      switch (status) {
        case 'pending': return 'bg-yellow-900/30 text-yellow-200 border-yellow-700';
        case 'approved': return 'bg-blue-900/30 text-blue-200 border-blue-700';
        case 'rejected': return 'bg-red-900/30 text-red-200 border-red-700';
        case 'completed': return 'bg-green-900/30 text-green-200 border-green-700';
        default: return 'bg-gray-700 text-gray-300 border-gray-600';
      }
    } else {
      switch (status) {
        case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
        case 'approved': return 'bg-blue-100 text-blue-800 border-blue-200';
        case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
        case 'completed': return 'bg-green-100 text-green-800 border-green-200';
        default: return 'bg-gray-100 text-gray-800 border-gray-200';
      }
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'approved': return <CheckCircle className="w-4 h-4" />;
      case 'rejected': return <XCircle className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };


  const renderDashboard = () => (
      <>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mb-8">
          <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-5 shadow-lg">
            <div className="flex items-center">
              <div className="p-3 rounded-xl bg-blue-500/20 text-blue-400 border border-blue-500/30">
                <Users className="w-6 h-6" />
              </div>
              <div className="mr-4">
                <p className="text-xs font-medium text-gray-400">کل کاربران</p>
                <p className="text-xl font-black text-white mt-1">
                  {isLoadingStats ? '...' : (stats?.localUsers || 0).toLocaleString('fa-IR')}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-5 shadow-lg">
            <div className="flex items-center">
              <div className="p-3 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                <Crown className="w-6 h-6" />
              </div>
              <div className="mr-4">
                <p className="text-xs font-medium text-gray-400">کل خریدها</p>
                <p className="text-xl font-black text-white mt-1">
                  {isLoadingStats ? '...' : (stats?.totalPurchases || 0).toLocaleString('fa-IR')}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-5 shadow-lg">
            <div className="flex items-center">
              <div className="p-3 rounded-xl bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                <Clock className="w-6 h-6" />
              </div>
              <div className="mr-4">
                <p className="text-xs font-medium text-gray-400">در انتظار خرید</p>
                <p className="text-xl font-black text-white mt-1">
                  {isLoadingStats ? '...' : (stats?.pendingPurchases || 0).toLocaleString('fa-IR')}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-5 shadow-lg">
            <div className="flex items-center">
              <div className="p-3 rounded-xl bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                <MessageSquare className="w-6 h-6" />
              </div>
              <div className="mr-4">
                <p className="text-xs font-medium text-gray-400">تیکت‌های باز</p>
                <p className="text-xl font-black text-white mt-1">
                  {isLoadingStats ? '...' : (stats?.openTickets || 0).toLocaleString('fa-IR')}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-5 shadow-lg">
            <div className="flex items-center">
              <div className="p-3 rounded-xl bg-purple-500/20 text-purple-400 border border-purple-500/30">
                <DollarSign className="w-6 h-6" />
              </div>
              <div className="mr-4">
                <p className="text-xs font-medium text-gray-400">درآمد کل</p>
                <p className="text-xl font-black text-white mt-1">
                  {isLoadingStats ? '...' : formatPrice(stats?.totalRevenue || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* نمودار درآمد روزانه */}
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} rounded-xl p-6 shadow-lg border mb-8`}
        >
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>درآمد روزانه — ۳۰ روز اخیر</h3>
              {hoveredDay ? (
                <p className="text-xs mt-1 text-blue-500 dark:text-blue-400 font-bold">
                  درآمد تاریخ {formatChartDate(hoveredDay.date)}: <span className="text-emerald-500">{formatPrice(hoveredDay.revenue)}</span> ({hoveredDay.count} خرید)
                </p>
              ) : (
                <div className={`flex flex-wrap gap-x-3 gap-y-1 text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span>این ماه: <span className="font-bold text-emerald-500">{formatPrice(stats?.currentMonthRevenue || 0)}</span></span>
                  <span>ماه قبل: <span className="font-bold">{formatPrice(stats?.lastMonthRevenue || 0)}</span></span>
                  <span>رشد: <span className={`font-bold ${(stats?.monthlyGrowth||0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>{(stats?.monthlyGrowth||0) >= 0 ? '+' : ''}{stats?.monthlyGrowth||0}%</span></span>
                </div>
              )}
            </div>
            <div className={`flex items-center gap-1 text-sm font-bold ${(stats?.monthlyGrowth || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              <TrendingUp className="w-4 h-4" />
              <span>{(stats?.monthlyGrowth||0) >= 0 ? '+' : ''}{stats?.monthlyGrowth||0}%</span>
            </div>
          </div>

          {isLoadingChart ? (
              <div className="flex items-center justify-center" style={{height:'160px'}}>
                <RefreshCw className={`w-6 h-6 animate-spin ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
              </div>
          ) : dailyRevenue.length > 0 ? (() => {
            const maxRevenue = Math.max(...dailyRevenue.map(d => d.revenue), 1);
            const CHART_H = 140; // px
            return (
                <div>
                  <div className="overflow-x-auto pb-2">
                    <div style={{ display: 'flex', alignItems: 'flex-end', height: `${CHART_H + 24}px`, gap: '3px', minWidth: `${dailyRevenue.length * 22}px` }}>
                      {dailyRevenue.map((day, i) => {
                        const barH = day.revenue > 0 ? Math.max(Math.round((day.revenue / maxRevenue) * CHART_H), 6) : 2;
                        const isToday = i === dailyRevenue.length - 1;
                        return (
                            <div 
                              key={i} 
                              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '1 0 18px', position: 'relative', cursor: 'pointer' }} 
                              onMouseEnter={() => setHoveredDay(day)}
                              onMouseLeave={() => setHoveredDay(null)}
                            >
                              {/* bar */}
                              <div
                                  style={{ width: '100%', height: `${barH}px`, borderRadius: '3px 3px 0 0' }}
                                  className={`transition-all duration-200 ${
                                      day.revenue === 0
                                          ? (isDarkMode ? 'bg-gray-700' : 'bg-gray-100')
                                          : isToday
                                              ? 'bg-emerald-400 hover:bg-emerald-300'
                                              : (isDarkMode ? 'bg-blue-500 hover:bg-blue-400' : 'bg-blue-500 hover:bg-blue-400')
                                  }`}
                              />
                              {/* date label every 5 bars */}
                              {i % 5 === 0 && (
                                  <span
                                      style={{ fontSize: '9px', marginTop: '4px', lineHeight: 1 }}
                                      className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}
                                  >{day.date.substring(5)}</span>
                              )}
                            </div>
                        );
                      })}
                    </div>
                  </div>
                  {/* legend + total */}
                  <div className={`flex flex-wrap items-center gap-3 mt-3 pt-3 border-t text-xs ${isDarkMode ? 'border-gray-700 text-gray-400' : 'border-gray-100 text-gray-500'}`}>
                    <div className="flex items-center gap-1"><span style={{width:'10px',height:'10px',borderRadius:'2px',background:'#3b82f6',display:'inline-block'}} /> روزهای معمول</div>
                    <div className="flex items-center gap-1"><span style={{width:'10px',height:'10px',borderRadius:'2px',background:'#34d399',display:'inline-block'}} /> امروز</div>
                    <div className="flex items-center gap-1"><span style={{width:'10px',height:'10px',borderRadius:'2px',background: isDarkMode ? '#374151' : '#f3f4f6',display:'inline-block'}} /> بدون درآمد</div>
                    <span className="mr-auto font-semibold">جمع: {formatPrice(dailyRevenue.reduce((s,d)=>s+d.revenue,0))}</span>
                  </div>
                </div>
            );
          })() : (
              <div className={`flex items-center justify-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} style={{height:'160px'}}>
                <BarChart3 className="w-8 h-8 ml-2" />
                <span>هنوز داده‌ای ثبت نشده</span>
              </div>
          )}
        </motion.div>

        {/* Recent Purchases */}
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} rounded-xl p-6 shadow-lg border mb-8`}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>آخرین خریدها</h3>
            <button
                onClick={() => setActiveTab('purchases')}
                className={`text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm font-medium`}
            >
              مشاهده همه
            </button>
          </div>

          {isLoadingStats ? (
              <div className="text-center py-8">
                <div className="spinner w-8 h-8 mx-auto mb-4"></div>
                <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>در حال بارگذاری...</p>
              </div>
          ) : recentPurchases.length > 0 ? (
              <div className="space-y-4">
                {recentPurchases.map((purchase) => (
                    <div key={purchase.id} className={`flex items-center justify-between p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-green-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-medium text-sm">
                      {purchase.user?.username?.charAt(0)}
                          </span>
                        </div>
                        <div className="mr-4">
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {purchase.user?.username}
                          </p>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{purchase.rankType}</p>
                        </div>
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-green-600 dark:text-green-400">{formatPrice(purchase.amount)}</p>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{formatDate(purchase.createdAt)}</p>
                      </div>
                      <div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      purchase.status === 'pending' ? (isDarkMode ? 'bg-yellow-900/30 text-yellow-200 border-yellow-700' : 'bg-yellow-100 text-yellow-800') :
                          purchase.status === 'approved' ? (isDarkMode ? 'bg-green-900/30 text-green-200 border-green-700' : 'bg-green-100 text-green-800') :
                              purchase.status === 'completed' ? (isDarkMode ? 'bg-blue-900/30 text-blue-200 border-blue-700' : 'bg-blue-100 text-blue-800') :
                                  (isDarkMode ? 'bg-red-900/30 text-red-200 border-red-700' : 'bg-red-100 text-red-800')
                  }`}>
                        {purchase.status === 'pending' && 'در انتظار'}
                    {purchase.status === 'approved' && 'تایید شده'}
                    {purchase.status === 'completed' && 'تکمیل شده'}
                    {purchase.status === 'rejected' && 'رد شده'}
                      </span>
                      </div>
                    </div>
                ))}
              </div>
          ) : (
              <div className="text-center py-8">
                <Package className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>هیچ خریدی یافت نشد</p>
              </div>
          )}
        </motion.div>
      </>
  );

  const renderUserManagement = () => {
    // Filter users based on search and status
    const filteredUsers = users.filter(user => {
      const matchesSearch =
          user.username?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
          user.gameUsername?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
          user.phone?.includes(userSearchTerm);

      const matchesStatus = userStatusFilter === 'all' ||
          (userStatusFilter === 'active' && user.isActive) ||
          (userStatusFilter === 'inactive' && !user.isActive);

      return matchesSearch && matchesStatus;
    });

    const getStatusColor = (isActive) => {
      if (isDarkMode) {
        return isActive
            ? 'bg-green-900/30 text-green-200 border-green-700'
            : 'bg-red-900/30 text-red-200 border-red-700';
      } else {
        return isActive
            ? 'bg-green-100 text-green-800'
            : 'bg-red-100 text-red-800';
      }
    };

    const getStatusIcon = (isActive) => {
      return isActive ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />;
    };

    const getStatusText = (isActive) => {
      return isActive ? 'فعال' : 'غیرفعال';
    };

    return (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>مدیریت کاربران</h2>
            <div className="flex items-center space-x-4 space-x-reverse">
              <div className="relative">
                <Search className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                <input
                    type="text"
                    placeholder="جستجو در کاربران..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    className={`pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
                />
              </div>
              <select
                  value={userStatusFilter}
                  onChange={(e) => setUserStatusFilter(e.target.value)}
                  className={`px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
              >
                <option value="all">همه کاربران</option>
                <option value="active">فعال</option>
                <option value="inactive">غیرفعال</option>
              </select>
            </div>
          </div>

          {/* Users Table */}
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg overflow-hidden`}>
            {isLoadingUsers ? (
                <div className="p-8 text-center">
                  <div className="spinner w-8 h-8 mx-auto mb-4"></div>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>در حال بارگذاری کاربران...</p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <tr>
                      <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        کاربر
                      </th>
                      <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        نام کاربری
                      </th>
                      <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        شماره موبایل
                      </th>
                      <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        وضعیت
                      </th>
                      <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        تاریخ عضویت
                      </th>
                      <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        آخرین ورود
                      </th>
                      <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        عملیات
                      </th>
                    </tr>
                    </thead>
                    <tbody className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {filteredUsers.map((user) => (
                        <tr key={user.id} className={`hover:${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-green-500 flex items-center justify-center">
                              <span className="text-white font-medium">
                                {user.username?.charAt(0)}
                          </span>
                                </div>
                              </div>
                              <div className="mr-4">
                                <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {user.username}
                                </div>
                                <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  ID: {user.id}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{user.gameUsername}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{user.phone}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user.isActive)}`}>
                          {getStatusIcon(user.isActive)}
                          <span className="mr-1">{getStatusText(user.isActive)}</span>
                    </span>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {user.createdAt ? formatDate(new Date(user.createdAt)) : 'نامشخص'}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {user.lastLogin ? formatDate(new Date(user.lastLogin)) : 'هرگز'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2 space-x-reverse">
                              <button
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setShowUserModal(true);
                                  }}
                                  className={`text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300`}
                                  title="مشاهده جزئیات"
                              >
                                <Eye className="w-4 h-4" />
                              </button>

                              <button
                                  onClick={() => handleDeleteUser(user.id)}
                                  className={`text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300`}
                                  title="حذف کاربر"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                    ))}
                    </tbody>
                  </table>
                </div>
            )}

            {filteredUsers.length === 0 && !isLoadingUsers && (
                <div className="text-center py-12">
                  <Users className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>هیچ کاربری یافت نشد</p>
                </div>
            )}
          </div>
        </div>
    );
  };

  const renderPurchaseManagement = () => {
    const filteredPurchases = purchases.filter(purchase => {
      const matchesSearch =
          purchase.user?.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          purchase.user?.gameUsername?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = selectedStatus === 'all' || purchase.status === selectedStatus;
      return matchesSearch && matchesStatus;
    });

    const totalPages = Math.ceil(filteredPurchases.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentPurchases = filteredPurchases.slice(startIndex, endIndex);

    const getStatusColor = (status) => {
      if (isDarkMode) {
        switch (status) {
          case 'pending': return 'bg-yellow-900/30 text-yellow-200 border-yellow-700';
          case 'approved': return 'bg-green-900/30 text-green-200 border-green-700';
          case 'rejected': return 'bg-red-900/30 text-red-200 border-red-700';
          case 'completed': return 'bg-blue-900/30 text-blue-200 border-blue-700';
          default: return 'bg-gray-700 text-gray-300 border-gray-600';
        }
      } else {
        switch (status) {
          case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
          case 'approved': return 'bg-green-100 text-green-800 border-green-200';
          case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
          case 'completed': return 'bg-blue-100 text-blue-800 border-blue-200';
          default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
      }
    };

    const getStatusIcon = (status) => {
      switch (status) {
        case 'pending': return <Clock className="w-3 h-3 mr-1" />;
        case 'approved': return <CheckCircle className="w-3 h-3 mr-1" />;
        case 'rejected': return <XCircle className="w-3 h-3 mr-1" />;
        case 'completed': return <CheckCircle className="w-3 h-3 mr-1" />;
        default: return <AlertCircle className="w-3 h-3 mr-1" />;
      }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
        >
          {/* Filters and Search */}
          <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} rounded-xl p-6 shadow-lg border`}>
            <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Filter className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  <select
                      value={selectedStatus}
                      onChange={(e) => {
                        setSelectedStatus(e.target.value);
                        setCurrentPage(1);
                      }}
                      className={`border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                  >
                    <option value="all">همه وضعیت‌ها</option>
                    <option value="pending">در انتظار</option>
                    <option value="approved">تایید شده</option>
                    <option value="rejected">رد شده</option>
                    <option value="completed">تکمیل شده</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Search className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                <input
                    type="text"
                    placeholder="جستجو در خریدها..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className={`border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
                />
              </div>
            </div>
          </div>

          {/* Purchases Table */}
          <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} rounded-xl shadow-lg border overflow-hidden`}>
            {isLoadingPurchases ? (
                <div className="p-8 text-center">
                  <div className="spinner w-8 h-8 mx-auto mb-4"></div>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>در حال بارگذاری خریدها...</p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                  <table className={`min-w-full divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <tr>
                      <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        کاربر
                      </th>
                      <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        رنک
                      </th>
                      <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        مبلغ
                      </th>
                      <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        وضعیت
                      </th>
                      <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        تاریخ
                      </th>
                      <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        عملیات
                      </th>
                    </tr>
                    </thead>
                    <tbody className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {currentPurchases.map((purchase) => (
                        <tr key={purchase.id} className={`hover:${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                                {(purchase.gameUsername || purchase.user?.username || 'ک')?.charAt(0)?.toUpperCase()}
                              </div>
                              <div className="mr-3">
                                <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {purchase.gameUsername || purchase.user?.username || 'کاربر'}
                                </div>
                                <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  @{purchase.gameUsername || purchase.user?.username || 'ثبت نشده'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isDarkMode ? 'bg-blue-900/30 text-blue-200 border-blue-700' : 'bg-blue-100 text-blue-800'}`}>
                      {purchase.rankType}
                    </span>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {formatPrice(purchase.amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(purchase.status)}`}>
                      {getStatusIcon(purchase.status)}
                      <span className="mr-1">
                        {purchase.status === 'pending' && 'در انتظار'}
                        {purchase.status === 'approved' && 'تایید شده'}
                        {purchase.status === 'rejected' && 'رد شده'}
                        {purchase.status === 'completed' && 'تکمیل شده'}
                      </span>
                    </span>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {formatDate(purchase.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                                onClick={() => {
                                  setSelectedPurchase(purchase);
                                  setShowModal(true);
                                }}
                                className={`text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 flex items-center gap-1`}
                            >
                              <Eye className="w-4 h-4" />
                              مشاهده
                            </button>
                          </td>
                        </tr>
                    ))}
                    </tbody>
                  </table>
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className={`px-6 py-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      نمایش {((currentPage - 1) * itemsPerPage) + 1} تا {Math.min(currentPage * itemsPerPage, filteredPurchases.length)} از {filteredPurchases.length} نتیجه
                      <span className="text-gray-500 mr-2 font-mono text-xs">({currentPage} / {totalPages})</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-1 justify-center max-w-full">
                      <button
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                          className={`px-2 py-1.5 border rounded-lg text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed ${
                              isDarkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'
                          }`}
                          title="صفحه اول"
                      >
                        اول
                      </button>
                      <button
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                          className={`px-2.5 py-1.5 border rounded-lg text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed ${
                              isDarkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        قبلی
                      </button>

                      {getPaginationPages(currentPage, totalPages).map((page, idx) => {
                        if (page === '...') {
                          return (
                            <span key={`dots-${idx}`} className="px-2 py-1 text-gray-500 text-xs font-mono">
                              ...
                            </span>
                          );
                        }
                        return (
                          <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`min-w-[32px] h-8 px-2 border rounded-lg text-xs font-bold transition-all ${
                                  currentPage === page
                                      ? 'bg-blue-600 text-white border-blue-500 shadow-md shadow-blue-500/20'
                                      : isDarkMode
                                          ? 'border-gray-700 text-gray-300 hover:bg-gray-700'
                                          : 'border-gray-300 hover:bg-gray-50'
                              }`}
                          >
                            {page}
                          </button>
                        );
                      })}

                      <button
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                          className={`px-2.5 py-1.5 border rounded-lg text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed ${
                              isDarkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        بعدی
                      </button>
                      <button
                          onClick={() => setCurrentPage(totalPages)}
                          disabled={currentPage === totalPages}
                          className={`px-2 py-1.5 border rounded-lg text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed ${
                              isDarkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'
                          }`}
                          title="صفحه آخر"
                      >
                        آخر
                      </button>
                    </div>
                  </div>
                </div>
            )}

            {filteredPurchases.length === 0 && !isLoadingPurchases && (
                <div className="text-center py-12">
                  <Package className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>هیچ خریدی یافت نشد</p>
                </div>
            )}
          </div>
        </motion.div>
    );
  };

  const renderDiscountCodeManagement = () => (
      <div className="space-y-6">
        {/* Header */}
        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} rounded-xl p-6 shadow-lg border`}>
          <div className="flex items-center justify-between mb-6">
            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>مدیریت کدهای تخفیف</h3>
            <button
                onClick={() => {
                  setShowDiscountCodeModal(true);
                  setSelectedDiscountCode(null);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus className="w-4 h-4" />
              افزودن کد تخفیف
            </button>
          </div>

          {/* Stats Cards */}
          {discountCodeStats && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4`}>
                  <div className="flex items-center">
                    <Package className={`w-6 h-6 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'} mr-3`} />
                    <div>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>کل کدها</p>
                      <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {discountCodeStats.totalCodes || 0}
                      </p>
                    </div>
                  </div>
                </div>
                <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4`}>
                  <div className="flex items-center">
                    <CheckCircle className={`w-6 h-6 ${isDarkMode ? 'text-green-400' : 'text-green-600'} mr-3`} />
                    <div>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>کدهای فعال</p>
                      <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {discountCodeStats.activeCodes || 0}
                      </p>
                    </div>
                  </div>
                </div>
                <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4`}>
                  <div className="flex items-center">
                    <TrendingUp className={`w-6 h-6 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'} mr-3`} />
                    <div>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>کل استفاده</p>
                      <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {discountCodeStats.totalUsage || 0}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
          )}
        </div>

        {/* Discount Codes Table */}
        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} rounded-xl shadow-lg border overflow-hidden`}>
          {isLoadingDiscountCodes ? (
              <div className="p-8 text-center">
                <div className="spinner w-8 h-8 mx-auto mb-4"></div>
                <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>در حال بارگذاری کدهای تخفیف...</p>
              </div>
          ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <tr>
                    <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      کد تخفیف
                    </th>
                    <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      توضیحات
                    </th>
                    <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      نوع تخفیف
                    </th>
                    <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      مقدار
                    </th>
                    <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      استفاده
                    </th>
                    <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      وضعیت
                    </th>
                    <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      عملیات
                    </th>
                  </tr>
                  </thead>
                  <tbody className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  {discountCodes.map((code) => (
                      <tr key={code.id} className={`hover:${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {code.code}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {code.description}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          code.discountType === 'percentage'
                              ? (isDarkMode ? 'bg-blue-900/30 text-blue-200 border-blue-700' : 'bg-blue-100 text-blue-800')
                              : (isDarkMode ? 'bg-green-900/30 text-green-200 border-green-700' : 'bg-green-100 text-green-800')
                      }`}>
                        {code.discountType === 'percentage' ? 'درصدی' : 'ثابت'}
                      </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {code.discountType === 'percentage' ? `${code.discountValue}%` : `${code.discountValue.toLocaleString('fa-IR')} تومان`}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {code.usedCount} / {code.maxUses === -1 ? '∞' : code.maxUses}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          code.isActive
                              ? (isDarkMode ? 'bg-green-900/30 text-green-200 border-green-700' : 'bg-green-100 text-green-800')
                              : (isDarkMode ? 'bg-red-900/30 text-red-200 border-red-700' : 'bg-red-100 text-red-800')
                      }`}>
                        {code.isActive ? 'فعال' : 'غیرفعال'}
                      </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <button
                                onClick={() => {
                                  setSelectedDiscountCode(code);
                                  setShowDiscountCodeModal(true);
                                }}
                                className={`text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300`}
                                title="ویرایش"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => handleToggleDiscountCode(code.id)}
                                className={`${code.isActive ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'} hover:opacity-80`}
                                title={code.isActive ? 'غیرفعال کردن' : 'فعال کردن'}
                            >
                              {code.isActive ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                            </button>
                            <button
                                onClick={() => handleDeleteDiscountCode(code.id)}
                                className={`text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300`}
                                title="حذف"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                  ))}
                  </tbody>
                </table>
              </div>
          )}

          {discountCodes.length === 0 && !isLoadingDiscountCodes && (
              <div className="text-center py-12">
                <Package className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>هیچ کد تخفیفی یافت نشد</p>
              </div>
          )}
        </div>
      </div>
  );

  const renderTicketManagement = () => {
    const API = process.env.REACT_APP_API_URL || '';
    const PRIORITY_LABELS = { low: 'پایین', medium: 'متوسط', high: 'بالا' };
    const PRIORITY_COLORS = {
      low: 'bg-emerald-900/30 text-emerald-300 border-emerald-700',
      medium: 'bg-yellow-900/30 text-yellow-300 border-yellow-700',
      high: 'bg-red-900/30 text-red-300 border-red-700'
    };
    const STATUS_LABELS = {
      open: 'جدید',
      in_progress: 'در حال بررسی',
      waiting_user: 'پاسخ ارسال شد',
      waiting_admin: 'نیازمند پاسخ شما',
      resolved: 'حل شده',
      closed: 'بسته شده'
    };
    const STATUS_COLORS = {
      open: 'bg-emerald-950/80 text-emerald-300 border-emerald-500/50 shadow-sm shadow-emerald-500/10',
      in_progress: 'bg-cyan-950/80 text-cyan-300 border-cyan-500/50 shadow-sm shadow-cyan-500/10',
      waiting_user: 'bg-slate-800/80 text-slate-400 border-slate-700/60',
      waiting_admin: 'bg-amber-950/90 text-amber-300 border-amber-500/80 shadow-md shadow-amber-500/20 font-bold',
      resolved: 'bg-purple-950/80 text-purple-300 border-purple-500/50',
      closed: 'bg-gray-800/80 text-gray-400 border-gray-700'
    };
    const DEPT_LABELS = { management: 'مدیریت', technical: 'فنی و خرید' };

    return (
      <div className="space-y-6" dir="rtl">
        {/* Filters and Header */}
        <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-5 flex flex-col md:flex-row items-center justify-between gap-4 shadow-xl">
          <div>
            <h2 className="text-2xl font-black text-white flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-emerald-400" />
              <span>مدیریت تیکت‌ها</span>
            </h2>
            <p className="text-gray-400 text-xs mt-1">تیکت‌های نیازمند اقدام در بالای لیست و پاسخ‌داده‌شده‌ها در انتها نمایش داده می‌شوند</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={ticketFilter.status}
              onChange={e => { const f={...ticketFilter, status:e.target.value}; setTicketFilter(f); fetchAdminTickets(f, 1); setAdminTicketsPage(1); }}
              className="bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded-xl text-sm focus:outline-none focus:border-emerald-500 transition-colors"
            >
              <option value="all">همه وضعیت‌ها</option>
              <option value="waiting_admin">⚡ نیازمند پاسخ (اولویت اول)</option>
              <option value="open">✨ تیکت‌های جدید</option>
              <option value="in_progress">در حال بررسی</option>
              <option value="waiting_user">پاسخ پشتیبانی داده شده</option>
              <option value="resolved">حل شده</option>
              <option value="closed">بسته شده</option>
            </select>
            <select
              value={ticketFilter.department}
              onChange={e => { const f={...ticketFilter, department:e.target.value}; setTicketFilter(f); fetchAdminTickets(f, 1); setAdminTicketsPage(1); }}
              className="bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded-xl text-sm focus:outline-none focus:border-emerald-500 transition-colors"
            >
              <option value="all">همه بخش‌ها</option>
              <option value="technical">فنی و خرید</option>
              <option value="management">مدیریت</option>
            </select>
            <select
              value={ticketFilter.priority}
              onChange={e => { const f={...ticketFilter, priority:e.target.value}; setTicketFilter(f); fetchAdminTickets(f, 1); setAdminTicketsPage(1); }}
              className="bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded-xl text-sm focus:outline-none focus:border-emerald-500 transition-colors"
            >
              <option value="all">همه اولویت‌ها</option>
              <option value="high">بالا</option>
              <option value="medium">متوسط</option>
              <option value="low">پایین</option>
            </select>
          </div>
        </div>

        {/* Tickets Grid */}
        {isLoadingTickets && adminTickets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-gray-400">در حال بارگذاری تیکت‌ها...</p>
          </div>
        ) : adminTickets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-gray-800/30 rounded-2xl border border-gray-800/50">
            <MessageSquare className="w-16 h-16 text-gray-600 mb-4" />
            <p className="text-gray-400 text-lg">هیچ تیکتی با این مشخصات یافت نشد</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {adminTickets.map(ticket => {
              const isUrgent = ticket.status === 'waiting_admin';
              const isWaitingUser = ticket.status === 'waiting_user';

              return (
                <div
                  key={ticket.id}
                  onClick={() => fetchTicketDetail(ticket.id)}
                  className={`border rounded-2xl p-5 cursor-pointer transition-all duration-300 flex flex-col group relative overflow-hidden backdrop-blur-xl ${
                    isUrgent
                      ? 'bg-amber-950/20 border-amber-500/50 hover:border-amber-400 hover:shadow-xl hover:shadow-amber-500/10'
                      : isWaitingUser
                      ? 'bg-gray-900/40 border-gray-800 opacity-80 hover:opacity-100 hover:border-gray-700'
                      : 'bg-gray-800/60 border-gray-700/60 hover:bg-gray-800 hover:border-emerald-500/50 hover:shadow-lg hover:shadow-emerald-500/5'
                  }`}
                >
                  <div className={`absolute top-0 left-0 w-full h-1 ${
                    isUrgent 
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500' 
                      : 'opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-r from-emerald-500 to-cyan-500'
                  }`} />
                  
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 text-xs font-mono font-bold">#{ticket.id}</span>
                      <span className={`text-xs px-2.5 py-1 rounded-full border flex items-center gap-1.5 ${STATUS_COLORS[ticket.status] || STATUS_COLORS.open}`}>
                        {isUrgent && (
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                          </span>
                        )}
                        <span>{STATUS_LABELS[ticket.status] || ticket.status}</span>
                      </span>
                    </div>
                    <button
                      onClick={(e) => handleDeleteTicket(ticket.id, e)}
                      className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/20 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                      title="حذف تیکت"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <h3 className="text-white font-bold mb-2 line-clamp-1 group-hover:text-emerald-400 transition-colors text-base">
                    {ticket.subject}
                  </h3>
                  
                  {ticket.lastMessage && (
                    <p className="text-gray-400 text-xs line-clamp-2 mb-4 flex-1 leading-relaxed bg-black/20 p-2 rounded-xl border border-white/5">
                      {ticket.lastMessage}
                    </p>
                  )}

                  <div className="mt-auto pt-3 border-t border-gray-700/50 flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-xs text-emerald-300 font-bold">
                          {ticket.ownerUsername?.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-gray-300 text-xs font-medium">{ticket.ownerUsername}</span>
                      </div>
                      <div className="flex items-center gap-1 text-[11px] text-gray-400 bg-white/5 px-2 py-0.5 rounded-lg border border-white/5">
                        <MessageSquare className="w-3 h-3 text-cyan-400" />
                        <span>{ticket.messageCount || 1}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-[10px] text-gray-500 pt-1">
                      <div className="flex items-center gap-1.5">
                        <span className="bg-purple-900/30 text-purple-300 border border-purple-700/60 text-[10px] px-1.5 py-0.5 rounded-full">
                          {DEPT_LABELS[ticket.department] || ticket.department}
                        </span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${PRIORITY_COLORS[ticket.priority]}`}>
                          {PRIORITY_LABELS[ticket.priority]}
                        </span>
                      </div>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(ticket.updatedAt || ticket.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {adminTicketsTotalPages > 1 && (
          <div className="flex flex-wrap justify-center items-center gap-1.5 mt-8">
            <button
              onClick={() => { setAdminTicketsPage(1); fetchAdminTickets(ticketFilter, 1); }}
              disabled={adminTicketsPage <= 1}
              className="px-2.5 py-1.5 bg-gray-800 border border-gray-700 text-white rounded-xl disabled:opacity-40 hover:bg-gray-700 transition-colors text-xs font-medium"
              title="صفحه اول"
            >
              اول
            </button>
            <button
              onClick={() => { const p = Math.max(1, adminTicketsPage - 1); setAdminTicketsPage(p); fetchAdminTickets(ticketFilter, p); }}
              disabled={adminTicketsPage <= 1}
              className="px-3 py-1.5 bg-gray-800 border border-gray-700 text-white rounded-xl disabled:opacity-40 hover:bg-gray-700 transition-colors text-xs font-medium"
            >
              قبلی
            </button>
            <div className="flex flex-wrap items-center justify-center gap-1">
              {getPaginationPages(adminTicketsPage, adminTicketsTotalPages).map((p, idx) => {
                if (p === '...') {
                  return (
                    <span key={`dots-t-${idx}`} className="px-2 py-1 text-gray-500 text-xs font-mono">
                      ...
                    </span>
                  );
                }
                return (
                  <button
                    key={p}
                    onClick={() => { setAdminTicketsPage(p); fetchAdminTickets(ticketFilter, p); }}
                    className={`min-w-[32px] h-8 px-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center ${
                      adminTicketsPage === p
                        ? 'bg-emerald-600 text-white border border-emerald-500 shadow-lg shadow-emerald-500/20'
                        : 'bg-gray-800 border border-gray-700 text-gray-400 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => { const p = Math.min(adminTicketsTotalPages, adminTicketsPage + 1); setAdminTicketsPage(p); fetchAdminTickets(ticketFilter, p); }}
              disabled={adminTicketsPage >= adminTicketsTotalPages}
              className="px-3 py-1.5 bg-gray-800 border border-gray-700 text-white rounded-xl disabled:opacity-40 hover:bg-gray-700 transition-colors text-xs font-medium"
            >
              بعدی
            </button>
            <button
              onClick={() => { setAdminTicketsPage(adminTicketsTotalPages); fetchAdminTickets(ticketFilter, adminTicketsTotalPages); }}
              disabled={adminTicketsPage >= adminTicketsTotalPages}
              className="px-2.5 py-1.5 bg-gray-800 border border-gray-700 text-white rounded-xl disabled:opacity-40 hover:bg-gray-700 transition-colors text-xs font-medium"
              title="صفحه آخر"
            >
              آخر
            </button>
          </div>
        )}

        {/* Ticket Modal */}
        <AnimatePresence>
          {selectedTicket && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-black/80 backdrop-blur-sm"
              onClick={() => { setSelectedTicket(null); setTicketMessages([]); setAdminReplyText(''); setAdminSelectedImage(null); setAdminImagePreview(null); }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.15, ease: "easeOut" }}
                className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-3xl h-auto max-h-[85vh] sm:max-h-[90vh] flex flex-col shadow-2xl overflow-hidden relative"
                onClick={e => e.stopPropagation()}
              >
              {/* Modal Header */}
              <div className="p-5 border-b border-gray-800 bg-gray-900/95 sticky top-0 z-10">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-gray-500 text-xs font-mono bg-gray-800 px-2 py-1 rounded-lg">#{selectedTicket.id}</span>
                      <span className={`text-xs px-2.5 py-1 rounded-full border ${STATUS_COLORS[selectedTicket.status] || STATUS_COLORS.open}`}>
                        {STATUS_LABELS[selectedTicket.status] || selectedTicket.status}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-1">{selectedTicket.subject}</h3>
                    <p className="text-gray-400 text-sm flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      {selectedTicket.ownerUsername}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-3">
                    <button 
                      onClick={() => { setSelectedTicket(null); setTicketMessages([]); setAdminReplyText(''); setAdminSelectedImage(null); setAdminImagePreview(null); }}
                      className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-xl transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                    <select
                      value={selectedTicket.status}
                      onChange={e => handleAdminTicketStatus(selectedTicket.id, e.target.value)}
                      className="bg-gray-800 border border-gray-700 text-white px-3 py-1.5 rounded-xl text-sm focus:outline-none focus:border-emerald-500 transition-colors"
                    >
                      <option value="open">باز</option>
                      <option value="in_progress">در حال بررسی</option>
                      <option value="waiting_user">پاسخ پشتیبانی داده شد</option>
                      <option value="waiting_admin">در انتظار پاسخ پشتیبانی</option>
                      <option value="resolved">حل شده</option>
                      <option value="closed">بسته شده</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Chat Messages */}
              <div ref={adminChatContainerRef} className="flex-1 overflow-y-auto p-5 space-y-4 bg-[#0a0f18] scroll-smooth">
                {ticketMessages.map((msg, i) => {
                  const currentDateDay = getMessageDateDay(msg.createdAt);
                  const prevDateDay = i > 0 ? getMessageDateDay(ticketMessages[i - 1].createdAt) : null;
                  const showDateHeader = i === 0 || currentDateDay !== prevDateDay;

                  return (
                    <React.Fragment key={msg.id}>
                      {showDateHeader && (
                        <div className="flex justify-center my-4">
                          <span className="bg-gray-800/80 text-gray-400 text-xs px-3 py-1 rounded-full border border-gray-700/50 font-medium">
                            {currentDateDay}
                          </span>
                        </div>
                      )}
                      <div className={`flex ${msg.senderType === 'admin' ? 'justify-start' : 'justify-end'}`}>
                        <div className={`max-w-[85%] rounded-2xl px-4 py-3 space-y-2 ${
                          msg.senderType === 'admin'
                            ? 'bg-gray-800 border border-gray-700 rounded-tr-sm'
                            : 'bg-emerald-900/20 border border-emerald-500/20 rounded-tl-sm'
                        }`}>
                          <div className="flex items-center justify-between gap-4">
                            <span className={`text-sm font-medium ${msg.senderType === 'admin' ? 'text-emerald-400' : 'text-cyan-400'}`}>
                              {msg.senderName}
                              {msg.senderType === 'admin' && <span className="text-gray-500 font-normal mr-1 text-xs">(پشتیبانی)</span>}
                            </span>
                            <span className="text-[10px] text-gray-500 font-mono bg-gray-900/50 px-1.5 py-0.5 rounded">{formatMessageTime(msg.createdAt)}</span>
                          </div>
                          
                          {msg.message && <p className="text-gray-200 text-sm leading-relaxed break-words whitespace-pre-wrap">{msg.message}</p>}
                          
                          {msg.image && (
                            <div className="relative group cursor-pointer overflow-hidden rounded-xl border border-gray-700 mt-2">
                              <img
                                src={`${API}${msg.image}`}
                                alt="پیوست"
                                className="max-w-xs max-h-64 object-cover rounded-xl transition-transform duration-300 group-hover:scale-105"
                                onClick={() => setAdminActiveZoomImage(`${API}${msg.image}`)}
                              />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity" onClick={() => setAdminActiveZoomImage(`${API}${msg.image}`)}>
                                <Eye className="w-8 h-8 text-white drop-shadow-lg" />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
                {ticketMessages.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-full text-gray-600">
                    <MessageSquare className="w-12 h-12 mb-3" />
                    <p>هیچ پیامی در این تیکت وجود ندارد.</p>
                  </div>
                )}
              </div>

              {/* Reply Input */}
              {selectedTicket.status !== 'closed' ? (
                <div className="p-4 border-t border-gray-800 bg-gray-900">
                  {adminImagePreview && (
                    <div className="flex items-center gap-3 bg-gray-800 p-2 rounded-xl mb-3 border border-gray-700">
                      <img src={adminImagePreview} alt="پیش‌نمایش" className="w-12 h-12 object-cover rounded-lg" />
                      <span className="text-xs text-gray-300 flex-1 truncate">{adminSelectedImage?.name}</span>
                      <button
                        onClick={() => { setAdminSelectedImage(null); setAdminImagePreview(null); if (adminFileRef.current) adminFileRef.current.value = ''; }}
                        className="p-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <div className="flex items-end gap-3">
                    <input
                      type="file"
                      ref={adminFileRef}
                      onChange={e => {
                        const file = e.target.files[0];
                        if (!file) return;
                        setAdminSelectedImage(file);
                        setAdminImagePreview(URL.createObjectURL(file));
                      }}
                      accept="image/*"
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => adminFileRef.current?.click()}
                      className="p-3 bg-gray-800 border border-gray-700 hover:border-emerald-500 hover:text-emerald-400 rounded-xl text-gray-400 transition-colors shrink-0"
                      title="ارسال تصویر"
                    >
                      <ImageIcon className="w-5 h-5" />
                    </button>
                    <textarea
                      value={adminReplyText}
                      onChange={e => setAdminReplyText(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAdminTicketReply(); } }}
                      placeholder="متن پاسخ شما..."
                      rows={1}
                      className="flex-1 bg-gray-800 border border-gray-700 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-emerald-500 resize-y min-h-[48px] max-h-32 transition-colors placeholder-gray-500"
                    />
                    <button
                      onClick={handleAdminTicketReply}
                      disabled={!adminReplyText.trim() && !adminSelectedImage}
                      className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0 flex items-center gap-2"
                    >
                      ارسال پاسخ
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-4 border-t border-gray-800 bg-gray-900 text-center flex items-center justify-center gap-2 text-red-400/80">
                  <Lock className="w-4 h-4" />
                  <span className="text-sm font-medium">این تیکت بسته شده است و امکان ارسال پاسخ وجود ندارد.</span>
                </div>
              )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Zoom Image Modal */}
        {adminActiveZoomImage && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md" onClick={() => setAdminActiveZoomImage(null)}>
            <div className="relative max-w-5xl max-h-[95vh]" onClick={e => e.stopPropagation()}>
              <button
                onClick={() => setAdminActiveZoomImage(null)}
                className="absolute -top-4 -right-4 z-10 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
              >
                <X className="w-6 h-6" />
              </button>
              <img src={adminActiveZoomImage} alt="بزرگنمایی" className="max-w-full max-h-[90vh] object-contain rounded-xl" />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
      <div className="min-h-screen bg-[#050811] text-white pt-24 pb-12 relative overflow-hidden" dir="rtl">
        {/* پس‌زمینه زنده با هاله‌های زمردی و سیان */}
        <div className="fixed inset-0 pointer-events-none z-0">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-emerald-900/20 rounded-full blur-[140px]" />
          <div className="absolute top-[40%] right-0 w-[600px] h-[400px] bg-cyan-900/15 rounded-full blur-[160px]" />
          <div className="absolute bottom-0 left-0 w-[600px] h-[400px] bg-emerald-950/20 rounded-full blur-[150px]" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Header */}
          <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-10"
          >
            <div className="w-20 h-20 bg-white/10 rounded-3xl p-2 flex items-center justify-center mx-auto mb-4 shadow-xl shadow-emerald-500/20 border border-white/20">
              <img src="/favicon.ico" alt="AvalonMC Logo" className="w-full h-full object-contain" />
            </div>
            <h1 className="text-3xl md:text-5xl font-black text-white mb-2">
              پنل مدیریت پیشرفته
            </h1>
            <p className="text-gray-400 text-sm">
              مدیریت کامل خریدهای رنک، کاربران، کدهای تخفیف و تیکت‌ها
            </p>
          </motion.div>

          {/* Main Navigation Tabs */}
          <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white/5 backdrop-blur-xl rounded-3xl p-3 shadow-2xl border border-white/10 mb-8"
          >
            <div className="flex flex-wrap gap-2">
              {canAccessDashboard && (
                <button
                    onClick={() => setActiveTab('dashboard')}
                    className={`flex-1 min-w-[140px] px-5 py-3 rounded-2xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                        activeTab === 'dashboard'
                            ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20'
                            : 'bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-white/10'
                    }`}
                >
                  <BarChart3 className="w-4 h-4" />
                  <span>داشبورد</span>
                </button>
              )}
              {canAccessUsers && (
                <button
                    onClick={() => setActiveTab('users')}
                    className={`flex-1 min-w-[140px] px-5 py-3 rounded-2xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                        activeTab === 'users'
                            ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20'
                            : 'bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-white/10'
                    }`}
                >
                  <Users className="w-4 h-4" />
                  <span>مدیریت کاربران</span>
                </button>
              )}
              {canAccessPurchases && (
                <button
                    onClick={() => setActiveTab('purchases')}
                    className={`flex-1 min-w-[140px] px-5 py-3 rounded-2xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                        activeTab === 'purchases'
                            ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20'
                            : 'bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-white/10'
                    }`}
                >
                  <Crown className="w-4 h-4" />
                  <span>مدیریت خریدها</span>
                </button>
              )}
              {canAccessDiscountCodes && (
                <button
                    onClick={() => setActiveTab('discountCodes')}
                    className={`flex-1 min-w-[140px] px-5 py-3 rounded-2xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                        activeTab === 'discountCodes'
                            ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20'
                            : 'bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-white/10'
                    }`}
                >
                  <Package className="w-4 h-4" />
                  <span>کدهای تخفیف</span>
                </button>
              )}
              {canAccessTickets && (
                <button
                    onClick={() => setActiveTab('tickets')}
                    className={`flex-1 min-w-[140px] px-5 py-3 rounded-2xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                        activeTab === 'tickets'
                            ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20'
                            : 'bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-white/10'
                    }`}
                >
                  <MessageSquare className="w-4 h-4" />
                  <span>تیکت‌ها</span>
                </button>
              )}
            </div>
          </motion.div>

          {/* Tab Content */}
          {activeTab === 'dashboard' && canAccessDashboard && renderDashboard()}
          {activeTab === 'users' && canAccessUsers && renderUserManagement()}
          {activeTab === 'purchases' && canAccessPurchases && renderPurchaseManagement()}
          {activeTab === 'discountCodes' && canAccessDiscountCodes && renderDiscountCodeManagement()}
          {activeTab === 'tickets' && canAccessTickets && renderTicketManagement()}
        </div>

        {/* Purchase Detail Modal - ساده و مینیمال */}
        {showModal && selectedPurchase && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowModal(false)}>
              <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  className={`${isDarkMode ? 'bg-gray-900' : 'bg-white'} rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-xl`}
                  onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 p-5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                      <Crown className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-800 dark:text-white">جزئیات خرید</h3>
                      <p className="text-sm text-gray-500">تیکت #{selectedPurchase.id}</p>
                    </div>
                  </div>
                  <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-all">
                    <XCircle className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                  {/* اطلاعات اصلی */}
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">نام کاربری</p>
                        <p className="text-base font-semibold text-gray-800 dark:text-white">{selectedPurchase.user?.username || 'نامشخص'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">نام کاربری در سرور</p>
                        <p className="text-base font-semibold text-emerald-600 dark:text-emerald-400">@{selectedPurchase.gameUsername || 'نامشخص'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">رنک درخواستی</p>
                        <p className="text-base font-semibold text-gray-800 dark:text-white">{selectedPurchase.rankType}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">مبلغ پرداختی</p>
                        <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{formatPrice(selectedPurchase.amount)} تومان</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">تاریخ خرید</p>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{formatDate(selectedPurchase.createdAt)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">وضعیت</p>
                        <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(selectedPurchase.status)}`}>
                {getStatusIcon(selectedPurchase.status)}
                          {selectedPurchase.status === 'pending' && 'در انتظار'}
                          {selectedPurchase.status === 'approved' && 'تایید شده'}
                          {selectedPurchase.status === 'rejected' && 'رد شده'}
                          {selectedPurchase.status === 'completed' && 'تکمیل'}
              </span>
                      </div>
                    </div>
                  </div>

                  {/* رسید پرداخت */}
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-5">
                    <h4 className="text-base font-bold text-gray-800 dark:text-white mb-3 text-center">رسید پرداخت</h4>
                    {selectedPurchase.receiptImage ? (
                        <div className="relative group cursor-pointer" onClick={() => window.open(selectedPurchase.receiptImage, '_blank')}>
                          <img src={selectedPurchase.receiptImage} alt="رسید پرداخت" className="w-full rounded-lg shadow-md" />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 rounded-lg transition-all flex items-center justify-center">
                            <Eye className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-all" />
                          </div>
                        </div>
                    ) : (
                        <div className="text-center py-8">
                          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">رسید پرداخت آپلود نشده است</p>
                        </div>
                    )}
                  </div>

                  {/* دکمه‌های مدیریت */}
                  <div className="flex gap-3">
                    <button
                        onClick={() => setNotesModal({ open: true, status: 'approved', title: 'تایید خرید', color: 'green' })}
                        disabled={isLoading || selectedPurchase.status === 'approved' || selectedPurchase.status === 'completed'}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" /> تایید
                    </button>
                    <button
                        onClick={() => setNotesModal({ open: true, status: 'rejected', title: 'رد خرید', color: 'red', required: true })}
                        disabled={isLoading || selectedPurchase.status === 'rejected' || selectedPurchase.status === 'completed'}
                        className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2"
                    >
                      <XCircle className="w-4 h-4" /> رد
                    </button>
                    <button
                        onClick={() => setNotesModal({ open: true, status: 'completed', title: 'تکمیل و تحویل', color: 'blue' })}
                        disabled={isLoading || selectedPurchase.status === 'completed'}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" /> تکمیل
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
        )}

        {/* مودال یادداشت ادمین */}
        {notesModal.open && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]" onClick={() => setNotesModal({ ...notesModal, open: false })}>
              <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`${isDarkMode ? 'bg-gray-900' : 'bg-white'} rounded-2xl max-w-lg w-full p-6 shadow-xl`}
                  onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    {notesModal.color === 'green' && <CheckCircle className="w-6 h-6 text-emerald-500" />}
                    {notesModal.color === 'red' && <XCircle className="w-6 h-6 text-red-500" />}
                    {notesModal.color === 'blue' && <CheckCircle className="w-6 h-6 text-blue-500" />}
                    <h3 className="text-lg font-bold text-gray-800 dark:text-white">{notesModal.title}</h3>
                  </div>
                  <button onClick={() => setNotesModal({ ...notesModal, open: false })} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full">
                    <XCircle className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {notesModal.required ? 'دلیل این عمل (الزامی)' : 'یادداشت (اختیاری)'}
                  </label>

                  {/* متن‌های پیشفرض */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {notesModal.status === 'rejected' && (
                        <>
                          <button
                              type="button"
                              onClick={() => setAdminNote('رسید ارسالی جعلی یا ویرایش‌شده به نظر می‌رسد. خرید رد شد.')}
                              className="text-xs px-3 py-1.5 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/70 transition-all"
                          >
                            رسید فیک
                          </button>
                          <button
                              type="button"
                              onClick={() => setAdminNote('مبلغ واریزی با قیمت رنک مطابقت ندارد.')}
                              className="text-xs px-3 py-1.5 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/70 transition-all"
                          >
                            مبلغ اشتباه
                          </button>
                          <button
                              type="button"
                              onClick={() => setAdminNote('رسید پرداخت آپلود نشده است.')}
                              className="text-xs px-3 py-1.5 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/70 transition-all"
                          >
                            بدون رسید
                          </button>
                        </>
                    )}
                    {notesModal.status === 'completed' && (
                        <>
                          <button
                              type="button"
                              onClick={() => setAdminNote(`سلام ${selectedPurchase?.gameUsername || 'کاربر'} عزیز\nمتشکر از خریدتون! رنک ${selectedPurchase?.rankType || ''} با موفقیت ست شد.\nهر هفته می‌تونید با دستور زیر کیت رو دریافت کنید:\n/ekit ${(selectedPurchase?.rankType || '').toLowerCase()}`)}
                              className="text-xs px-3 py-1.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-700 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/70 transition-all"
                          >
                            تکمیل با کیت
                          </button>
                          <button
                              type="button"
                              onClick={() => setAdminNote(`سلام ${selectedPurchase?.gameUsername || 'کاربر'} عزیز\nمتشکر از خریدتون! رنک ${selectedPurchase?.rankType || ''} با موفقیت ست شد.`)}
                              className="text-xs px-3 py-1.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-700 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/70 transition-all"
                          >
                            تکمیل ساده
                          </button>
                        </>
                    )}
                  </div>

                  <textarea
                      value={adminNote}
                      onChange={(e) => setAdminNote(e.target.value)}
                      placeholder={notesModal.required ? 'دلیل رد درخواست را وارد کنید...' : 'یادداشت خود را وارد کنید...'}
                      className={`w-full h-32 px-4 py-3 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200'} focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500 transition-all resize-none`}
                  />
                  {notesModal.required && adminNote.trim() === '' && (
                      <p className="text-red-500 text-xs mt-2">نوشتن دلیل الزامی است!</p>
                  )}
                </div>

                <div className="flex gap-3">
                  <button onClick={() => setNotesModal({ ...notesModal, open: false })} className="flex-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 py-3 rounded-xl font-semibold text-sm transition-all">
                    انصراف
                  </button>
                  <button
                      onClick={() => {
                        if (notesModal.required && !adminNote.trim()) {
                          toast.error('لطفاً دلیل را وارد کنید');
                          return;
                        }
                        handleStatusChange(selectedPurchase.id, notesModal.status, adminNote.trim() || 'بدون یادداشت');
                        setNotesModal({ open: false });
                        setAdminNote('');
                      }}
                      disabled={isLoading}
                      className={`flex-1 py-3 rounded-xl font-semibold text-sm text-white transition-all flex items-center justify-center gap-2 ${
                          notesModal.color === 'green' ? 'bg-emerald-600 hover:bg-emerald-700' :
                              notesModal.color === 'red' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
                      } ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                  >
                    {isLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'تأیید و اعمال'}
                  </button>
                </div>
              </motion.div>
            </div>
        )}


        {/* User Detail Modal */}
        {showUserModal && selectedUser && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-semibold text-gray-800">جزئیات کاربر</h3>
                    <button
                        onClick={() => setShowUserModal(false)}
                        className="text-gray-400 hover:text-gray-600"
                    >
                      <XCircle className="w-6 h-6" />
                    </button>
                  </div>

                  <div className="space-y-6">
                    {/* User Profile */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center mb-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-xl">
                          {selectedUser.username.charAt(0)}
                        </div>
                        <div className="mr-4">
                          <h4 className="text-lg font-semibold text-gray-800">
                            {selectedUser.username}
                          </h4>
                          <p className="text-gray-600">@{selectedUser.gameUsername}</p>
                        </div>
                      </div>
                    </div>

                    {/* User Information */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-800 mb-3">اطلاعات شخصی</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-gray-600">نام کاربری:</label>
                          <p className="font-medium">{selectedUser.username}</p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-600">نام کاربری:</label>
                          <p className="font-medium">{selectedUser.gameUsername}</p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-600">شماره موبایل:</label>
                          <p className="font-medium">{selectedUser.phone || 'ثبت نشده'}</p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-600">وضعیت حساب:</label>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                              selectedUser.isActive ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-800 border-red-200'
                          }`}>
                         {selectedUser.isActive ? 'فعال' : 'غیرفعال'}
                       </span>
                        </div>
                        <div>
                          <label className="text-sm text-gray-600">نقش:</label>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                         {selectedUser.role === 'admin' ? 'ادمین' : 'کاربر'}
                       </span>
                        </div>
                      </div>
                    </div>

                    {/* Account Information */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-800 mb-3">اطلاعات حساب</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-gray-600">تاریخ عضویت:</label>
                          <p className="font-medium">{formatDate(selectedUser.createdAt)}</p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-600">آخرین ورود:</label>
                          <p className="font-medium">{selectedUser.lastLogin ? formatDate(selectedUser.lastLogin) : 'هرگز'}</p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-600">شناسه کاربر:</label>
                          <p className="font-medium">#{selectedUser.id}</p>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-4 border-t border-gray-200">
                      <button
                          onClick={() => handleDeleteUser(selectedUser.id)}
                          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        حذف کاربر
                      </button>
                      <button
                          onClick={() => setShowUserModal(false)}
                          className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                      >
                        <XCircle className="w-4 h-4" />
                        بستن
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
        )}

        {/* Discount Code Modal - با پشتیبانی از دارک مود */}
        {showDiscountCodeModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowDiscountCodeModal(false)}>
              <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className={`${isDarkMode ? 'bg-gray-900' : 'bg-white'} rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto`}
                  onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {selectedDiscountCode ? 'ویرایش کد تخفیف' : 'افزودن کد تخفیف جدید'}
                    </h3>
                    <button
                        onClick={() => setShowDiscountCodeModal(false)}
                        className={`${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                    >
                      <XCircle className="w-6 h-6" />
                    </button>
                  </div>

                  <form onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const discountData = {
                      code: formData.get('code'),
                      description: formData.get('description'),
                      discountType: formData.get('discountType'),
                      discountValue: parseInt(formData.get('discountValue')),
                      maxUses: parseInt(formData.get('maxUses')) || -1,
                      minAmount: parseInt(formData.get('minAmount')) || 0,
                      applicableRanks: formData.getAll('applicableRanks'),
                      validUntil: formData.get('validUntil') || null
                    };

                    if (selectedDiscountCode) {
                      handleUpdateDiscountCode(selectedDiscountCode.id, discountData);
                    } else {
                      handleCreateDiscountCode(discountData);
                    }
                  }}>
                    <div className="space-y-4">
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          کد تخفیف *
                        </label>
                        <input
                            type="text"
                            name="code"
                            required
                            defaultValue={selectedDiscountCode?.code || ''}
                            className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                isDarkMode
                                    ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            placeholder="مثال: SUMMER2024"
                        />
                      </div>

                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          توضیحات *
                        </label>
                        <textarea
                            name="description"
                            required
                            defaultValue={selectedDiscountCode?.description || ''}
                            className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                isDarkMode
                                    ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            rows="3"
                            placeholder="توضیحات کد تخفیف..."
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            نوع تخفیف *
                          </label>
                          <select
                              name="discountType"
                              required
                              defaultValue={selectedDiscountCode?.discountType || 'percentage'}
                              className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                  isDarkMode
                                      ? 'bg-gray-800 border-gray-700 text-white'
                                      : 'bg-white border-gray-300 text-gray-900'
                              }`}
                          >
                            <option value="percentage">درصدی</option>
                            <option value="fixed">ثابت (تومان)</option>
                          </select>
                        </div>
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            مقدار تخفیف *
                          </label>
                          <input
                              type="number"
                              name="discountValue"
                              required
                              min="1"
                              defaultValue={selectedDiscountCode?.discountValue || ''}
                              className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                  isDarkMode
                                      ? 'bg-gray-800 border-gray-700 text-white'
                                      : 'bg-white border-gray-300 text-gray-900'
                              }`}
                              placeholder="مثال: 20"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            حداکثر استفاده
                          </label>
                          <input
                              type="number"
                              name="maxUses"
                              min="-1"
                              defaultValue={selectedDiscountCode?.maxUses || -1}
                              className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                  isDarkMode
                                      ? 'bg-gray-800 border-gray-700 text-white'
                                      : 'bg-white border-gray-300 text-gray-900'
                              }`}
                              placeholder="-1 برای نامحدود"
                          />
                        </div>
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            حداقل مبلغ (تومان)
                          </label>
                          <input
                              type="number"
                              name="minAmount"
                              min="0"
                              defaultValue={selectedDiscountCode?.minAmount || 0}
                              className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                  isDarkMode
                                      ? 'bg-gray-800 border-gray-700 text-white'
                                      : 'bg-white border-gray-300 text-gray-900'
                              }`}
                              placeholder="0 برای بدون محدودیت"
                          />
                        </div>
                      </div>

                      {/* ====== لیست کامل رنک‌ها ====== */}
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          رنک‌های قابل استفاده
                        </label>
                        <div className="space-y-2">
                          {['Lite', 'Vip', 'Mvp', 'Blitz', 'Nebula', 'Dragon', 'Sponsor', 'Eternity', 'Plutonium', 'Heaven', 'Crystal', 'Pillar', 'L-GroveStreet', 'L-Ballas', 'L-Ravex', 'L-Zyrex'].map((rank) => (
                              <label key={rank} className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="applicableRanks"
                                    value={rank}
                                    defaultChecked={selectedDiscountCode?.applicableRanks ?
                                        JSON.parse(selectedDiscountCode.applicableRanks).includes(rank) : true}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className={`mr-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{rank}</span>
                              </label>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          تاریخ انقضا
                        </label>
                        <input
                            type="datetime-local"
                            name="validUntil"
                            defaultValue={selectedDiscountCode?.validUntil ?
                                new Date(selectedDiscountCode.validUntil).toISOString().slice(0, 16) : ''}
                            className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                isDarkMode
                                    ? 'bg-gray-800 border-gray-700 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 pt-6 border-t border-gray-200 dark:border-gray-700 mt-4">
                      <button
                          type="button"
                          onClick={() => setShowDiscountCodeModal(false)}
                          className={`px-4 py-2 rounded-lg transition-colors ${
                              isDarkMode
                                  ? 'text-gray-400 hover:text-gray-200'
                                  : 'text-gray-600 hover:text-gray-800'
                          }`}
                      >
                        انصراف
                      </button>
                      <button
                          type="submit"
                          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center gap-2"
                      >
                        <Save className="w-4 h-4" />
                        {selectedDiscountCode ? 'بروزرسانی' : 'ایجاد'}
                      </button>
                    </div>
                  </form>
                </div>
              </motion.div>
            </div>
        )}
      </div>
  );
};



export default AdminPanel;
